// Team schedule — the employee's home-team published week. Mobile gets a
// day strip + per-position lists; desktop gets the read-only week grid
// (sticky position column, Sun–Sat with day totals). Employees can't read
// the roster, so the export menu gets employees=[].
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../auth/AuthProvider'
import {
  fetchDepartment,
  fetchPositions,
  fetchTeams,
  fetchTeamWeekShifts,
  fetchWeekDoc,
} from '../../api/reads'
import { EmptyState, ErrorBanner, Spinner } from '../../components/ui'
import { DayStrip, WeekPicker } from '../../components/time'
import { ShiftPill } from '../../components/ShiftPill'
import { ExportMenu } from '../../components/ExportMenu'
import { todayLocal, TZ, weekDates, weekKeyFor } from '../../lib/time'
import { formatDateLong, formatDateShort, formatHours } from '../../lib/format'
import { hoursByDay, round2 } from '../../lib/hours'
import { positionColor } from '../../lib/positionColors'
import { DateTime } from 'luxon'
import type { Employee, Shift } from '../../lib/types'

export default function TeamPage() {
  const { employee, email } = useAuth()
  if (!employee || !email) return null
  if (!employee.departmentId || !employee.homeTeamId) {
    return <EmptyState title="No home team yet" hint="Ask your director to assign you to a team." />
  }
  return (
    <Team
      employee={employee}
      email={email}
      departmentId={employee.departmentId}
      teamId={employee.homeTeamId}
    />
  )
}

/** Local media-query hook (no external dep). */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches)
  useEffect(() => {
    const mql = window.matchMedia(query)
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches)
    setMatches(mql.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [query])
  return matches
}

interface PositionGroup {
  positionId: string
  name: string
  color: string
  sortOrder: number
  shifts: Shift[]
}

function Team({
  employee,
  email,
  departmentId,
  teamId,
}: {
  employee: Employee
  email: string
  departmentId: string
  teamId: string
}) {
  const navigate = useNavigate()
  const isDesktop = useMediaQuery('(min-width: 768px)')
  const today = todayLocal()
  const [weekStart, setWeekStart] = useState(() => weekKeyFor(todayLocal()))
  const [selectedDay, setSelectedDay] = useState(() => todayLocal())

  const shiftsQuery = useQuery({
    queryKey: ['teamWeek', teamId, weekStart],
    queryFn: () => fetchTeamWeekShifts(teamId, weekStart),
  })
  const weekDocQuery = useQuery({
    queryKey: ['weekDoc', departmentId, weekStart],
    queryFn: () => fetchWeekDoc(departmentId, weekStart),
  })
  const referenceQuery = useQuery({
    queryKey: ['reference', departmentId],
    queryFn: async () => {
      const [teams, positions] = await Promise.all([fetchTeams(departmentId), fetchPositions(departmentId)])
      return { teams, positions }
    },
  })
  const departmentQuery = useQuery({
    queryKey: ['department', departmentId],
    queryFn: () => fetchDepartment(departmentId),
  })

  const shifts = useMemo(
    () => (shiftsQuery.data ?? []).filter((s) => s.status !== 'cancelled'),
    [shiftsQuery.data],
  )

  // Position groups in sortOrder; unknown positionIds get synthesized rows.
  const groups: PositionGroup[] = useMemo(() => {
    const positions = referenceQuery.data?.positions ?? []
    const map = new Map<string, PositionGroup>()
    for (const p of positions) {
      map.set(p.id, { positionId: p.id, name: p.name, color: positionColor(p), sortOrder: p.sortOrder, shifts: [] })
    }
    for (const s of shifts) {
      let g = map.get(s.positionId)
      if (!g) {
        g = {
          positionId: s.positionId,
          name: s.positionName,
          color: positionColor({ color: null, id: s.positionId }),
          sortOrder: Number.MAX_SAFE_INTEGER,
          shifts: [],
        }
        map.set(s.positionId, g)
      }
      g.shifts.push(s)
    }
    return [...map.values()]
      .filter((g) => g.shifts.length > 0)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
      .map((g) => ({ ...g, shifts: [...g.shifts].sort((a, b) => a.startAt.localeCompare(b.startAt)) }))
  }, [shifts, referenceQuery.data])

  const days = weekDates(weekStart)
  const dayTotals = hoursByDay(shifts)
  const markedDates = useMemo(
    () => new Set(shifts.filter((s) => s.assigneeEmail === email && s.status === 'assigned').map((s) => s.date)),
    [shifts, email],
  )
  const effectiveDay = days.includes(selectedDay) ? selectedDay : days.includes(today) ? today : days[0]!

  const weekDoc = weekDocQuery.data
  const department = departmentQuery.data
  const teamName = referenceQuery.data?.teams.find((t) => t.id === teamId)?.name

  if (shiftsQuery.isPending || referenceQuery.isPending) return <Spinner label="Loading the schedule…" />
  if (shiftsQuery.isError) return <ErrorBanner message="Couldn't load the team schedule. Try again." />

  const pillFor = (s: Shift) => (
    <ShiftPill
      key={s.id}
      shift={s}
      positionColor={groups.find((g) => g.positionId === s.positionId)?.color ?? positionColor({ color: null, id: s.positionId })}
      highlight={s.assigneeEmail === email}
      onClick={() => void navigate(`/shift/${s.id}`)}
    />
  )

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-bold text-interlochen">{teamName ?? 'Team schedule'}</h1>
          {department && <p className="text-xs text-stone">{department.name}</p>}
        </div>
        <div className="flex items-center gap-2">
          <WeekPicker weekStart={weekStart} onChange={setWeekStart} />
          <ExportMenu
            departmentId={departmentId}
            departmentName={department?.name ?? departmentId}
            weekStart={weekStart}
            shifts={shifts}
            teams={referenceQuery.data?.teams ?? []}
            employees={[]}
            thresholdHours={department?.otThresholdHours ?? employee.otThresholdHours ?? 40}
          />
        </div>
      </div>

      {shifts.length === 0 ? (
        <EmptyState
          title="Nothing published for this week yet"
          hint="Your team's schedule appears here once your supervisor publishes it."
        />
      ) : isDesktop ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-hairline bg-white shadow-card">
            <table className="w-full min-w-[980px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-hairline">
                  <th scope="col" className="sticky left-0 z-10 w-44 bg-white px-3 py-2 text-left align-bottom">
                    <span className="section-label">Position</span>
                  </th>
                  {days.map((d) => {
                    const dt = DateTime.fromISO(d, { zone: TZ })
                    return (
                      <th key={d} scope="col" className={`px-2 py-2 text-left align-bottom ${d === today ? 'bg-sky/15' : ''}`}>
                        <span className={`block text-xs uppercase ${d === today ? 'font-bold text-interlochen' : 'text-stone'}`}>
                          {dt.toFormat('ccc')} <span className="tnum">{dt.toFormat('L/d')}</span>
                        </span>
                        <span className="tnum block text-xs text-stone">
                          {formatHours(round2(dayTotals.get(d) ?? 0))}
                        </span>
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => {
                  const weeklyTotal = round2(g.shifts.reduce((sum, s) => sum + s.durationHours, 0))
                  return (
                    <tr key={g.positionId} className="border-b border-hairline last:border-b-0">
                      <th scope="row" className="sticky left-0 z-10 w-44 bg-white px-3 py-2 text-left align-top">
                        <span className="flex items-center gap-2 font-semibold text-ink">
                          <span className="h-3 w-1 rounded-full" style={{ backgroundColor: g.color }} aria-hidden />
                          {g.name}
                        </span>
                        <span className="tnum block pl-3 text-xs font-normal text-stone">
                          {formatHours(weeklyTotal)}
                        </span>
                      </th>
                      {days.map((d) => (
                        <td key={d} className={`min-w-[120px] px-1.5 py-1.5 align-top ${d === today ? 'bg-sky/10' : ''}`}>
                          <div className="space-y-1">{g.shifts.filter((s) => s.date === d).map(pillFor)}</div>
                        </td>
                      ))}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {weekDoc && weekDoc.events.length > 0 && <WeekEvents events={weekDoc.events} />}
        </>
      ) : (
        <>
          <DayStrip weekStart={weekStart} selected={effectiveDay} onSelect={setSelectedDay} markedDates={markedDates} />
          <h2 className="text-sm font-bold text-interlochen">{formatDateLong(effectiveDay)}</h2>
          {groups.every((g) => g.shifts.every((s) => s.date !== effectiveDay)) ? (
            <EmptyState title="No shifts this day" />
          ) : (
            <div className="space-y-4">
              {groups.map((g) => {
                const dayShifts = g.shifts.filter((s) => s.date === effectiveDay)
                if (dayShifts.length === 0) return null
                return (
                  <section key={g.positionId} aria-label={g.name}>
                    <h3 className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-ink">
                      <span className="h-3 w-1 rounded-full" style={{ backgroundColor: g.color }} aria-hidden />
                      {g.name}
                    </h3>
                    <div className="space-y-1.5">{dayShifts.map(pillFor)}</div>
                  </section>
                )
              })}
            </div>
          )}
          {weekDoc && weekDoc.events.length > 0 && <WeekEvents events={weekDoc.events} />}
        </>
      )}
    </div>
  )
}

function WeekEvents({ events }: { events: { date: string; title: string; details: string }[] }) {
  return (
    <section className="card" aria-label="Week events">
      <p className="section-label mb-2">This week</p>
      <ul className="space-y-1.5">
        {events.map((ev, i) => (
          <li key={`${ev.date}-${i}`} className="text-sm">
            <span className="tnum font-semibold text-interlochen">{formatDateShort(ev.date)}</span>{' '}
            <span className="font-semibold text-ink">{ev.title}</span>
            {ev.details && <span className="text-stone"> — {ev.details}</span>}
          </li>
        ))}
      </ul>
    </section>
  )
}
